import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, koKR } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  CssBaseline,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight, MoreVert, Search, Visibility } from '@mui/icons-material';
import { DialogTitleClose } from '../dialog';
import DocumentViewerDrawer from '../common/viewer/DocumentViewerDrawer';
import MobilePdfPreview from '../common/viewer/MobilePdfPreview';
import { createPreviewObjectUrl } from './SubMatFolderFileActions';
import { FormInputAutoComplete, FormInputText } from '../form';
import { filterGclientOptions, gclientOptionLabel, renderGclientOption, toGclientSelectOption } from './gclientSelectOption';
import { CustomNoRowsOverlay } from '../datagrid';
import { getGridHeight } from '../../constants/gridHeights';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as subMatFolderAlarmEmailActions from '../../store/subMatFolderAlarmEmail';
import * as gclientActions from '../../store/gclient';
import SubMatFolderExpiryDialog, { resolveFolderTaxType } from './SubMatFolderExpiryDialog';
import SubMatFolderAlarmSettingDialog from './SubMatFolderAlarmSettingDialog';
import SubMatTestComparisonDialog from './SubMatTestComparisonDialog';
import { parseSubMatUploadError } from './SubMatFolderUploadZone';
import { dateFormat } from '../../utils';
import {
  formatSubMatEndDate,
  formatSubMatExpiryStatusText,
  getSubMatExpiryStatus,
  isSubMatFolderDocInvalid,
  SubMatExpiryStatusChip,
} from './SubMatFolderExpiryStatus';
import '../../utils/Date';

const theme = createTheme();
const GRID_HEIGHT = getGridHeight('ONE_LINE_LAYER', 0);
const MOBILE_PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'unset', label: '미설정' },
  { value: 'expiring', label: '임박(7일)' },
  { value: 'expired', label: '만료' },
  { value: 'valid', label: '유효' },
  { value: 'none', label: '만료일 없음' },
];

const ROOT_KIND_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'GENERAL', label: '일반문서' },
  { value: 'CERTIFICATION', label: '인증서' },
  { value: 'TEST', label: '성적서' },
  { value: 'ETC', label: '기타' },
];

const toExplorerFolderPath = (folderPath) => {
  if (!folderPath) return '';
  return String(folderPath)
    .split(/\s*(?:›|\/)\s*/)
    .filter(Boolean)
    .join(' › ');
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const canMakeComparison = (row) => row?.rootKind === 'TEST' || row?.rootKind === 'ETC';

const stopGridEvent = (event) => {
  event.stopPropagation();
};

const RowActionsMenu = ({ row, onAttach, onCompare, onAlarm }) => {
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState(null);
  const comparable = canMakeComparison(row) && !isMobile;
  const hasComparison = Boolean(row.comparisonDocumentPath);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="더보기"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem onClick={() => { close(); onAttach(row); }}>첨부</MenuItem>
        {comparable && (
          <MenuItem onClick={() => { close(); onCompare(row); }}>
            {hasComparison ? '시험성과대비표 수정' : '시험성과대비표 만들기'}
          </MenuItem>
        )}
        <MenuItem onClick={() => { close(); onAlarm(row); }}>알람</MenuItem>
      </Menu>
    </>
  );
};

const ExpiryDateCell = ({ row, resolveTaxType, onSaved, onError }) => {
  const [endDate, setEndDate] = useState(row.endDate ? new Date(row.endDate) : null);
  const [validYN, setValidYN] = useState(row.validYN === true);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [taxType, setTaxType] = useState('');

  useEffect(() => {
    setEndDate(row.endDate ? new Date(row.endDate) : null);
    setValidYN(row.validYN === true);
    setDirty(false);
  }, [row.id, row.endDate, row.validYN]);

  useEffect(() => {
    let cancelled = false;
    resolveTaxType(row.folderId).then((code) => {
      if (!cancelled) setTaxType(code || '');
    });
    return () => { cancelled = true; };
  }, [resolveTaxType, row.folderId]);

  const handleSave = async () => {
    if (!validYN && !endDate) {
      onError('만료일을 입력하거나 「만료일 없음」을 선택하세요.');
      return;
    }
    setBusy(true);
    onError('');
    try {
      const payloadEnd = validYN
        ? null
        : dateFormat(new Date(endDate.getTime()).getDateWithEndHours());
      await subMatFolderActions.updateFileExpiryDirect(row.folderId, row.id, {
        endDate: payloadEnd,
        validYN: Boolean(validYN),
      });
      setDirty(false);
      setBusy(false);
      await onSaved?.();
    } catch (e) {
      setBusy(false);
      onError(e?.serverResponse?.message || e.message || '만료일을 저장하지 못했습니다.');
    }
  };

  const handleExtract = async () => {
    if (!taxType) return;
    setExtractBusy(true);
    onError('');
    try {
      const parsed = await subMatFolderActions.extractFileExpiryDirect(row.folderId, row.id, taxType);
      if (parsed) {
        setEndDate(new Date(parsed));
        setValidYN(false);
        setDirty(true);
      } else {
        onError('만료일을 읽지 못했습니다. 직접 입력한 뒤 저장하세요.');
      }
    } catch (_e) {
      onError('만료일을 읽지 못했습니다. 직접 입력한 뒤 저장하세요.');
    } finally {
      setExtractBusy(false);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      onMouseDown={stopGridEvent}
      onClick={stopGridEvent}
      onDoubleClick={stopGridEvent}
    >
      <TextField
        type="date"
        size="small"
        value={validYN ? '' : toDateInputValue(endDate)}
        disabled={validYN || busy || extractBusy}
        onChange={(e) => {
          const next = e.target.value;
          setValidYN(false);
          setEndDate(next ? new Date(`${next}T00:00:00`) : null);
          setDirty(true);
        }}
        inputProps={{ 'aria-label': `${row.fileName || '서류'} 만료일` }}
        sx={{
          width: 128,
          '& .MuiOutlinedInput-root': { height: 24, fontSize: '0.75rem' },
          '& .MuiOutlinedInput-input': { py: 0, px: '6px', height: 24, boxSizing: 'border-box' },
        }}
      />
      <FormControlLabel
        sx={{
          m: 0,
          '& .MuiFormControlLabel-label': { fontSize: '0.75rem', lineHeight: 1, whiteSpace: 'nowrap' },
          '& .MuiCheckbox-root': { p: 0, mr: 0.25 },
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
        control={(
          <Checkbox
            size="small"
            checked={validYN}
            disabled={busy || extractBusy}
            onChange={(e) => {
              setValidYN(e.target.checked);
              if (e.target.checked) setEndDate(null);
              setDirty(true);
            }}
            inputProps={{ 'aria-label': `${row.fileName || '서류'} 만료일 없음` }}
          />
        )}
        label="만료일 없음"
      />
      <Button
        variant="contained"
        size="small"
        disabled={!dirty || busy || extractBusy}
        onClick={handleSave}
        sx={{ minWidth: 36, height: 22, px: 0.75, py: 0, fontSize: '0.75rem', lineHeight: 1 }}
      >
        저장
      </Button>
      {taxType && (
        <Button
          variant="outlined"
          size="small"
          disabled={busy || extractBusy}
          onClick={handleExtract}
          sx={{ minWidth: 40, height: 22, px: 0.75, py: 0, fontSize: '0.75rem', lineHeight: 1 }}
        >
          {extractBusy ? '판독 중' : '판독'}
        </Button>
      )}
    </Stack>
  );
};

const defaultValues = {
  gclient: null,
  rootKind: '',
  expiryStatus: '',
  fileName: '',
};

/**
 * 서류점검. 데스크톱은 목록에서 만료일을 고치고, 모바일은 카드와 만료일·알람 탭을 유지한다.
 * 기본: 자기 거래선. ADMIN: 거래처 선택 가능.
 */
const SubMatFolderExpiryReviewManagement = () => {
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const isAdmin = sessionUser?.type === 'ADMIN';
  const { control, setValue, getValues } = useForm({ defaultValues });

  const [gclients, setGclients] = useState([]);
  const [loginGclientOption, setLoginGclientOption] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [mobilePage, setMobilePage] = useState(0);
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [comparisonTarget, setComparisonTarget] = useState(null);
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down('md'));
  const [alarmTarget, setAlarmTarget] = useState(null);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const previewUrlRef = useRef('');
  const attachInputRef = useRef(null);
  const attachRowRef = useRef(null);
  const taxTypeCacheRef = useRef(new Map());

  const selfId = sessionUser?.id || sessionUser?.gclientId;

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await gclientActions.selectValidAllDirect();
        if (cancelled) return;
        setGclients(list || []);
        if (selfId) {
          const self = (list || []).find((c) => c.id === selfId);
          if (self) {
            const option = toGclientSelectOption(self);
            setValue('gclient', option);
            setLoginGclientOption(option);
          }
        }
      } catch (_e) {
        if (!cancelled) setGclients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, selfId, setValue]);

  const loadRows = useCallback(async ({ resetPage = true } = {}) => {
    setLoaded(false);
    setError('');
    try {
      const gclient = getValues('gclient');
      const rootKind = getValues('rootKind');
      const expiryStatus = getValues('expiryStatus');
      const fileName = (getValues('fileName') || '').trim();
      const targetId = isAdmin ? (gclient?.id || selfId) : selfId;
      const list = await subMatFolderActions.selectExpiryReviewDirect({
        gclientId: targetId,
        rootKind: rootKind || undefined,
        expiryStatus: expiryStatus || undefined,
        fileName: fileName || undefined,
      });
      setRows((list || []).map((r) => ({
        ...r,
        id: r.id,
        expiryStatusKey: getSubMatExpiryStatus(r),
        endDateLabel: formatSubMatEndDate(r),
      })));
      if (resetPage) setMobilePage(0);
    } catch (e) {
      setRows([]);
      setError(e?.serverResponse?.message || e.message || '목록을 불러오지 못했습니다.');
    } finally {
      setLoaded(true);
    }
  }, [getValues, isAdmin, selfId]);

  useEffect(() => {
    if (!sessionUser) return;
    loadRows();
  }, [sessionUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSearch = () => {
    loadRows();
  };

  const handleChangeExpiryStatus = (e) => {
    setValue('expiryStatus', e.target.value);
    loadRows();
  };

  const handleChangeRootKind = (e) => {
    setValue('rootKind', e.target.value);
    loadRows();
  };

  const handleChangeGClient = (_e, newValue) => {
    setValue('gclient', newValue);
    loadRows();
  };

  const openAlarmDialog = useCallback(async (row) => {
    if (!row?.folderId) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    try {
      const alarms = await subMatFolderAlarmEmailActions.selectDirect({
        gclientId: row.gclientId,
        folderId: row.folderId,
      });
      window.setTimeout(() => {
        setAlarmTarget({
          folderId: row.folderId,
          gclientId: row.gclientId,
          alarms: alarms || [],
        });
      }, 0);
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '알람 설정을 불러오지 못했습니다.');
    }
  }, []);

  const handleDeleteAlarm = useCallback(async (id) => {
    if (!id) return;
    try {
      await subMatFolderAlarmEmailActions.removeDirect(id);
      loadRows();
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '알람을 삭제하지 못했습니다.');
    }
  }, [loadRows]);

  const openMobileSheet = useCallback(async (row, tab = 'date') => {
    if (!row?.id) return;
    let alarms = [];
    try {
      alarms = await subMatFolderAlarmEmailActions.selectDirect({
        gclientId: row.gclientId,
        folderId: row.folderId,
      });
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '알람 설정을 불러오지 못했습니다.');
    }
    setExpiryTarget({
      file: { ...row },
      folderId: row.folderId,
      gclientId: row.gclientId,
      alarms: alarms || [],
      mobileTab: tab,
    });
  }, []);

  const resolveTaxType = useCallback((folderId) => {
    if (!folderId) return Promise.resolve('');
    const cache = taxTypeCacheRef.current;
    if (!cache.has(folderId)) {
      cache.set(folderId, resolveFolderTaxType(folderId).catch(() => ''));
    }
    return cache.get(folderId);
  }, []);

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewUrl('');
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    revokePreviewUrl();
  }, [revokePreviewUrl]);

  const openFilePreview = useCallback(async (documentPath, title) => {
    if (!documentPath) {
      setError('미리볼 서류가 없습니다.');
      return;
    }
    try {
      const url = await createPreviewObjectUrl(documentPath);
      revokePreviewUrl();
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewTitle(title || '문서');
      setPreviewOpen(true);
    } catch (e) {
      setError(e.message || '미리보기를 열 수 없습니다.');
    }
  }, [revokePreviewUrl]);

  const openPreview = useCallback((row) => {
    openFilePreview(row?.documentPath, row?.fileName || '문서');
  }, [openFilePreview]);

  const openComparisonPreview = useCallback((row) => {
    if (!row?.comparisonDocumentPath) {
      setError('미리볼 시험성과대비표가 없습니다.');
      return;
    }
    const baseName = String(row.fileName || '성적서').replace(/\.pdf$/i, '');
    openFilePreview(row.comparisonDocumentPath, `${baseName}_성과대비표.pdf`);
  }, [openFilePreview]);

  const openComparison = useCallback((row) => {
    if (!row?.id || !row.folderId || !canMakeComparison(row)) return;
    setComparisonTarget({
      folder: { id: row.folderId, name: row.folderName || '' },
      reportFile: {
        id: row.id,
        fileName: row.fileName,
        documentPath: row.documentPath,
        comparisonFile: row.comparisonDocumentPath ? { documentPath: row.comparisonDocumentPath } : null,
      },
    });
  }, []);

  const attachPdf = useCallback(async (row, file) => {
    if (!row?.folderId || !file) return null;
    if (file.type && file.type !== 'application/pdf' && !String(file.name || '').toLowerCase().endsWith('.pdf')) {
      throw new Error('PDF 파일만 업로드할 수 있습니다.');
    }
    let uploaded;
    try {
      uploaded = await dispatch(subMatFolderActions.uploadFile(row.folderId, file));
    } catch (e) {
      const messages = await parseSubMatUploadError(e);
      throw new Error(messages[0] || '업로드에 실패했습니다.');
    }
    setExpiryTarget((prev) => {
      if (!prev?.file || prev.file.folderId !== row.folderId) return prev;
      if (prev.previewOnly) return null;
      return {
        ...prev,
        file: {
          ...prev.file,
          ...uploaded,
          folderId: row.folderId,
          folderName: prev.file.folderName,
          rootKind: prev.file.rootKind,
          gclientId: prev.file.gclientId || prev.gclientId,
          endDate: uploaded?.endDate || null,
          validYN: Boolean(uploaded?.validYN),
          comparisonDocumentPath: null,
        },
      };
    });
    await loadRows({ resetPage: false });
    return uploaded;
  }, [dispatch, loadRows]);

  const beginAttach = useCallback((row) => {
    if (!row?.folderId) return;
    attachRowRef.current = row;
    if (attachInputRef.current) {
      attachInputRef.current.value = '';
      attachInputRef.current.click();
    }
  }, []);

  const handleAttachInput = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const row = attachRowRef.current;
    attachRowRef.current = null;
    if (!file || !row) return;
    try {
      await attachPdf(row, file);
    } catch (e) {
      setError(e?.message || '업로드에 실패했습니다.');
    }
  };

  const columns = useMemo(() => {
    const cols = [];
    if (isAdmin) {
      cols.push({
        field: 'gclientName',
        headerName: '거래처',
        width: 160,
      });
    }
    cols.push(
      {
        field: 'rootKind',
        headerName: '서류종류',
        width: 100,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => subMatFolderActions.ROOT_KIND_LABEL[params.value] || params.value || '',
      },
      {
        field: 'folderPath',
        headerName: '폴더 경로',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (params) => toExplorerFolderPath(params.value),
        renderCell: (params) => {
          const path = params.value || '';
          if (!path) return '';
          const parts = path.split(' › ');
          return (
            <Tooltip title={path} followCursor>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  fontSize: 'inherit',
                }}
              >
                {parts.map((name, i) => (
                  <Box
                    key={`${i}-${name}`}
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
                  >
                    {i > 0 && (
                      <Box component="span" sx={{ mx: 0.5, color: 'text.secondary', flexShrink: 0 }}>
                        ›
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{
                        color: i === parts.length - 1 ? 'text.primary' : 'text.secondary',
                        fontWeight: i === parts.length - 1 ? 500 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'fileName',
        headerName: '파일명',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'documentPath',
        headerName: '서류',
        width: 72,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const fileName = params.row.fileName || '';
          if (!params.row.documentPath) return null;
          const invalid = isSubMatFolderDocInvalid(params.row);
          return (
            <Tooltip title={fileName ? `미리보기 · ${fileName}` : '미리보기'}>
              <IconButton
                size="small"
                aria-label="서류보기"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  openPreview(params.row);
                }}
                sx={{ color: invalid ? '#DB4E2E' : '#2EA2DE' }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'comparisonDocumentPath',
        headerName: '대비표',
        width: 72,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (!params.row.comparisonDocumentPath) return null;
          const invalid = isSubMatFolderDocInvalid(params.row);
          return (
            <Tooltip title="시험성과대비표 미리보기">
              <IconButton
                size="small"
                aria-label="시험성과대비표 미리보기"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  openComparisonPreview(params.row);
                }}
                sx={{ color: invalid ? '#DB4E2E' : '#2EA2DE' }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'endDateLabel',
        headerName: '만료일',
        width: 348,
        sortable: false,
        filterable: false,
        valueGetter: (params) => formatSubMatEndDate(params.row) || '',
        renderCell: (params) => (
          <ExpiryDateCell
            row={params.row}
            resolveTaxType={resolveTaxType}
            onSaved={() => loadRows({ resetPage: false })}
            onError={setError}
          />
        ),
      },
      {
        field: 'expiryStatusKey',
        headerName: '상태',
        width: 110,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => formatSubMatExpiryStatusText(params.row) || '',
        renderCell: (params) => {
          const invalid = isSubMatFolderDocInvalid(params.row);
          return (
            <Box component="span" sx={invalid ? { color: '#DB4E2E' } : undefined}>
              {params.value}
            </Box>
          );
        },
      },
      {
        field: 'alarmsSet',
        headerName: '설정된 알람',
        width: 210,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const alarms = params.row.alarms;
          if (!alarms || !Array.isArray(alarms) || alarms.length === 0) return null;
          return alarms.map((alarm, i) => (
            <Chip
              key={alarm.id || `${alarm.type}-${alarm.period}-${i}`}
              label={subMatFolderAlarmEmailActions.formatAlarmPeriodLabel(alarm)}
              color="primary"
              size="small"
              variant="outlined"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDelete={() => handleDeleteAlarm(alarm.id)}
              sx={{ mr: i === alarms.length - 1 ? 0 : 1 }}
            />
          ));
        },
      },
      {
        field: 'actions',
        headerName: '기능',
        width: 72,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <RowActionsMenu
            row={params.row}
            onAttach={beginAttach}
            onCompare={openComparison}
            onAlarm={openAlarmDialog}
          />
        ),
      },
    );
    return cols;
  }, [beginAttach, handleDeleteAlarm, isAdmin, loadRows, openAlarmDialog, openComparison, openComparisonPreview, openPreview, resolveTaxType]);

  const gclientOptions = useMemo(
    () => (gclients || []).map((c) => toGclientSelectOption(c)),
    [gclients],
  );

  const mobileRows = rows.slice(mobilePage * MOBILE_PAGE_SIZE, (mobilePage + 1) * MOBILE_PAGE_SIZE);
  const mobileFrom = rows.length === 0 ? 0 : mobilePage * MOBILE_PAGE_SIZE + 1;
  const mobileTo = Math.min(rows.length, (mobilePage + 1) * MOBILE_PAGE_SIZE);

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="false">
          <CssBaseline />
          <Box sx={isMobile ? {
            mt: 3,
            height: 'calc(100vh - 219px)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          } : { mt: 3 }}>
            {isMobile ? (
              <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
                {isAdmin && (
                  <FormInputAutoComplete
                    name="gclient"
                    control={control}
                    setValue={setValue}
                    defaultValue={loginGclientOption}
                    label="거래처"
                    noOptionsText="해당 거래처가 없습니다."
                    options={gclientOptions}
                    getOptionLabel={gclientOptionLabel}
                    filterOptions={filterGclientOptions}
                    renderOption={renderGclientOption}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    onCustomChange={handleChangeGClient}
                  />
                )}
                <FormInputText
                  select
                  name="rootKind"
                  control={control}
                  label="서류종류"
                  options={ROOT_KIND_OPTIONS}
                  onChange={handleChangeRootKind}
                />
                <FormInputText
                  select
                  name="expiryStatus"
                  control={control}
                  label="만료 상태"
                  options={STATUS_OPTIONS}
                  onChange={handleChangeExpiryStatus}
                />
                <FormInputText
                  name="fileName"
                  control={control}
                  label="파일명으로 검색하세요."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClickSearch();
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleClickSearch}
                  sx={{ height: 48 }}
                >
                  조회
                </Button>
              </Stack>
            ) : (
            <Grid container spacing={2}>
              {isAdmin && (
                <Grid item xs={12} sm={6} md={3} display="flex" justifyContent="flex-start" alignItems="center">
                  <FormInputAutoComplete
                    name="gclient"
                    control={control}
                    setValue={setValue}
                    defaultValue={loginGclientOption}
                    label="거래처"
                    noOptionsText="해당 거래처가 없습니다."
                    options={gclientOptions}
                    getOptionLabel={gclientOptionLabel}
                    filterOptions={filterGclientOptions}
                    renderOption={renderGclientOption}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    onCustomChange={handleChangeGClient}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={3} md={2} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  select
                  name="rootKind"
                  control={control}
                  label="서류종류"
                  options={ROOT_KIND_OPTIONS}
                  onChange={handleChangeRootKind}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  select
                  name="expiryStatus"
                  control={control}
                  label="만료 상태"
                  options={STATUS_OPTIONS}
                  onChange={handleChangeExpiryStatus}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={isAdmin ? 3 : 4} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  name="fileName"
                  control={control}
                  label="파일명으로 검색하세요."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClickSearch();
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={isAdmin ? 2 : 3}
                md={isAdmin ? 2 : 4}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleClickSearch}
                  sx={{ minWidth: 96 }}
                >
                  조회
                </Button>
              </Grid>
            </Grid>
            )}

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
            )}

            {isMobile ? (
              <Box sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                mt: 1.25,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'grey.100',
                borderRadius: 1,
                overflow: 'hidden',
              }}>
                {!loaded && <LinearProgress />}
                {loaded && rows.length === 0 && (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">조회된 서류가 없습니다.</Typography>
                  </Box>
                )}
                {rows.length > 0 && (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pt: 0.5, pb: 1 }}>
                    {mobileRows.map((row) => {
                      const path = toExplorerFolderPath(row.folderPath);
                      const kind = subMatFolderActions.ROOT_KIND_LABEL[row.rootKind] || row.rootKind || '';
                      const invalid = isSubMatFolderDocInvalid(row);
                      const unsetRequired = invalid && getSubMatExpiryStatus(row) === 'unset';
                      const alarms = Array.isArray(row.alarms) ? row.alarms : [];
                      const endDateText = row.endDateLabel && row.endDateLabel !== '없음' ? row.endDateLabel : '';
                      return (
                        <Paper
                          key={row.id}
                          elevation={0}
                          role="button"
                          onClick={() => openMobileSheet(row)}
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            cursor: 'pointer',
                            borderLeft: '3px solid',
                            borderLeftColor: invalid ? 'rgba(219, 78, 46, 0.45)' : 'rgba(46, 162, 222, 0.35)',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1, pl: 1.25 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                              {row.fileName || '(이름 없음)'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {kind && <Chip size="small" variant="outlined" label={kind} />}
                              {unsetRequired ? (
                                <Chip size="small" label="미설정" color="error" />
                              ) : (
                                <SubMatExpiryStatusChip file={row} />
                              )}
                              {endDateText && (
                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                  {endDateText}
                                </Typography>
                              )}
                            </Box>
                            {path && (
                              <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25, wordBreak: 'break-all' }}>
                                {path}
                              </Typography>
                            )}
                            {alarms.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {alarms.map((alarm, i) => (
                                  <Chip
                                    key={alarm.id || `${alarm.type}-${alarm.period}-${i}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    label={subMatFolderAlarmEmailActions.formatAlarmPeriodLabel(alarm)}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                                알람 없음
                              </Typography>
                            )}
                            </Box>
                            <Stack direction="row" alignItems="center" onClick={(event) => event.stopPropagation()}>
                              {row.documentPath && (
                                <Tooltip title="서류보기">
                                  <IconButton
                                    size="small"
                                    aria-label="서류보기"
                                    onClick={() => openMobileSheet(row)}
                                    sx={{ color: invalid ? '#DB4E2E' : '#2EA2DE' }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {row.comparisonDocumentPath && (
                                <Tooltip title="시험성과대비표 미리보기">
                                  <IconButton
                                    size="small"
                                    aria-label="시험성과대비표 미리보기"
                                    onClick={() => openComparisonPreview(row)}
                                    sx={{ color: invalid ? '#DB4E2E' : '#2EA2DE' }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <RowActionsMenu
                                row={row}
                                onAttach={beginAttach}
                                onCompare={openComparison}
                                onAlarm={(current) => openMobileSheet(current, 'alarm')}
                              />
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
                <Box sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 44,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}>
                  <IconButton
                    aria-label="이전 페이지"
                    disabled={!loaded || mobilePage <= 0}
                    onClick={() => setMobilePage((prev) => Math.max(prev - 1, 0))}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {rows.length === 0 ? '0건' : `${mobileFrom}–${mobileTo} / ${rows.length}건`}
                  </Typography>
                  <IconButton
                    aria-label="다음 페이지"
                    disabled={!loaded || (mobilePage + 1) * MOBILE_PAGE_SIZE >= rows.length}
                    onClick={() => setMobilePage((prev) => prev + 1)}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
            ) : (
            <div style={{ height: GRID_HEIGHT.FULL, width: '100%', marginTop: 10 }}>
              <DataGridPro
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                sx={{
                  fontSize: '0.85em',
                }}
                initialState={{ pinnedColumns: { right: ['actions'] } }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  loadingOverlay: LinearProgress,
                }}
                headerHeight={38}
                rowHeight={34}
                loading={!loaded}
                getRowId={(row) => row.id}
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 50, 100]}
                pagination
                disableRowSelectionOnClick
              />
            </div>
            )}
          </Box>
        </Container>
      </ThemeProvider>

      {/* DataGrid/ThemeProvider 밖에 두어 DatePicker 포커스 충돌 완화 (서류등록과 동일 계층) */}
      <input
        ref={attachInputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={handleAttachInput}
      />
      <SubMatFolderExpiryDialog
        open={Boolean(expiryTarget?.file)}
        file={expiryTarget?.file || null}
        folderId={expiryTarget?.folderId || expiryTarget?.file?.folderId}
        embedAlarm={isMobile}
        previewOnly={false}
        startTab={expiryTarget?.mobileTab || 'date'}
        gclientId={expiryTarget?.gclientId}
        alarms={expiryTarget?.alarms}
        onClose={() => setExpiryTarget(null)}
        onSaved={() => {
          setExpiryTarget(null);
          loadRows({ resetPage: false });
        }}
      />
      {isMobile ? (
        <Dialog
          open={previewOpen}
          onClose={closePreview}
          fullScreen
          PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
        >
          <DialogTitleClose
            onClose={closePreview}
            sx={{
              position: 'relative',
              pr: 6,
              fontSize: '18px !important',
              fontWeight: '600 !important',
              lineHeight: 1.3,
              padding: '16px 56px 16px 16px !important',
              '& .MuiIconButton-root[aria-label="close"]': {
                marginTop: '0 !important',
                top: '50%',
                transform: 'translateY(-50%)',
              },
            }}
          >
            <Box
              component="span"
              title={previewTitle || undefined}
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {previewTitle || '문서'}
            </Box>
          </DialogTitleClose>
          <DialogContent
            sx={{
              p: '0 !important',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {previewUrl && (
              <MobilePdfPreview src={previewUrl} title={previewTitle || '문서'} />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <DocumentViewerDrawer
          key={previewUrl || 'closed'}
          open={previewOpen}
          onClose={closePreview}
          url={previewUrl}
        />
      )}
      <SubMatTestComparisonDialog
        open={Boolean(comparisonTarget)}
        folder={comparisonTarget?.folder || null}
        reportFile={comparisonTarget?.reportFile || null}
        onClose={() => setComparisonTarget(null)}
        onSaved={() => {
          const reportId = comparisonTarget?.reportFile?.id;
          setComparisonTarget(null);
          if (reportId) {
            setExpiryTarget((prev) => {
              if (!prev?.file || prev.file.id !== reportId) return prev;
              return {
                ...prev,
                file: {
                  ...prev.file,
                  comparisonDocumentPath: prev.file.comparisonDocumentPath || 'saved',
                },
              };
            });
          }
          loadRows({ resetPage: false });
        }}
      />
      <SubMatFolderAlarmSettingDialog
        open={Boolean(alarmTarget?.folderId)}
        folderId={alarmTarget?.folderId}
        gclientId={alarmTarget?.gclientId}
        alarms={alarmTarget?.alarms}
        onClose={() => setAlarmTarget(null)}
        onSaved={() => {
          setAlarmTarget(null);
          loadRows();
        }}
      />
    </>
  );
};

export default SubMatFolderExpiryReviewManagement;
